// DashboardShell — task 7.2 of the dashboard-redesign spec (Requirement 3).
//
// Composes the four zones of the treasurer-facing landing page:
//
//   Header ...... Building_Selector (aggregate ↔ single-building pivot)
//   Banner ...... OnboardingBanner (visible iff buildings.length === 0)
//   KPI band .... 4 tiles — Arrears · Collection Rate · Reserve Fund · Cash at Bank
//   Middle band . 4 tiles — Aging · Reserve Movement · Compliance · Meetings
//   Activity .... 1 full-width tile — Recent Activity
//   Reserved .... Chatbot_Launcher_Slot (fixed 56×56, filled by help-docs-panel)
//
// See:
//   - `.kiro/specs/dashboard-redesign/design.md` §Layout / §Data-flow diagram
//   - `.kiro/specs/dashboard-redesign/requirements.md` §Requirement 3
//     (widget composition + independence).
//   - `.kiro/steering/design-language.md` — page canvas `#f3f4f6`, 32-px
//     gutter, responsive Tailwind grid tokens.
//
// Widget failure independence (Req 3.4, 3.6 / Property P4)
// --------------------------------------------------------
// Every widget already wraps itself in its own <ErrorBoundary> (Wave 3–5
// established this pattern). The shell adds a REDUNDANT boundary around
// each widget as belt-and-braces defence: if a catastrophic render bug
// somehow escapes a widget's own boundary, the shell boundary catches it
// and renders a minimal <WidgetError> placeholder in place of the tile.
// The surrounding header, the OnboardingBanner, the other seven widgets,
// and the ChatbotLauncherSlot MUST remain interactive.
//
// Responsive grid (Tailwind)
// --------------------------
// KPI band and middle band both use `grid-cols-1 sm:grid-cols-2
// lg:grid-cols-4` so the tile row collapses to 2 columns at tablet width
// and 1 column at mobile width. The activity band is always full-width.
//
// The shell is a pure presentation component — no data fetching, no
// mutation, no ownership of scope state. The parent (the `/dashboard`
// route) owns `scope` and the buildings list; the shell only threads them
// into the selector and widgets.

import React from "react";
import type { Building } from "@akfdev-com/shared";
import { ErrorBoundary } from "../../components/ui/ErrorBoundary";
import { BuildingSelector } from "./BuildingSelector";
import { ChatbotLauncherSlot } from "./ChatbotLauncherSlot";
import { OnboardingBanner } from "./OnboardingBanner";
import { WidgetError } from "./WidgetError";
import { Widget_Arrears } from "./widgets/Widget_Arrears";
import { Widget_CollectionRate } from "./widgets/Widget_CollectionRate";
import { Widget_ReserveFundPosition } from "./widgets/Widget_ReserveFundPosition";
import { Widget_CashAtBank } from "./widgets/Widget_CashAtBank";
import { Widget_ArrearsAging } from "./widgets/Widget_ArrearsAging";
import { Widget_ReserveFundMovement } from "./widgets/Widget_ReserveFundMovement";
import { Widget_UpcomingCompliance } from "./widgets/Widget_UpcomingCompliance";
import { Widget_UpcomingMeetings } from "./widgets/Widget_UpcomingMeetings";
import { Widget_RecentActivity } from "./widgets/Widget_RecentActivity";
import type { BuildingScope } from "./types";

/**
 * Props for {@link DashboardShell}.
 *
 * The parent owns the `scope` state and re-fetches every widget when it
 * changes. `buildings` is the tenant's list; the shell derives
 * "onboarding needed" as `buildings.length === 0` and forwards the same
 * list to `<BuildingSelector>` for the pivot dropdown.
 *
 * `sessionKey` is the hashed identifier used to namespace persisted
 * scope in `localStorage`. When `undefined` (e.g. sign-in still
 * resolving), persistence is disabled — the shell still renders.
 */
export interface DashboardShellProps {
  /** Current building scope — aggregate or single-building. */
  scope: BuildingScope;
  /** Fires when the user picks a new scope in the Building_Selector. */
  onScopeChange: (scope: BuildingScope) => void;
  /** Tenant's buildings list. Empty → OnboardingBanner is shown. */
  buildings: readonly Building[];
  /**
   * Hashed session key (SHA-256(ownerId) first 16 hex chars) that
   * namespaces the persisted scope. Undefined disables persistence.
   */
  sessionKey?: string;
}

/**
 * Belt-and-braces error boundary wrapping each widget so a catastrophic
 * render exception can't blank the surrounding page. Each widget already
 * has its own internal boundary; this outer one is defence-in-depth.
 * The fallback stays minimal — just a <WidgetError> placeholder card
 * — so the DOM shape stays consistent even in failure.
 */
function WidgetZoneBoundary({
  name,
  children,
}: {
  name: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <ErrorBoundary
      fallback={<WidgetError name={name} errorId="shell-boundary" />}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * The dashboard's presentational shell. Renders header + onboarding
 * banner + three widget bands + reserved chatbot slot.
 */
export function DashboardShell({
  scope,
  onScopeChange,
  buildings,
  sessionKey,
}: DashboardShellProps): JSX.Element {
  const showOnboarding = buildings.length === 0;

  return (
    <div
      data-testid="dashboard-shell"
      className="p-6 space-y-6 min-h-screen"
      style={{ backgroundColor: "#f3f4f6" }}
    >
      <header
        data-testid="dashboard-header"
        className="flex items-center justify-between gap-4"
      >
        <div className="flex-1 max-w-xs">
          <BuildingSelector
            buildings={buildings}
            value={scope}
            onChange={onScopeChange}
            sessionKey={sessionKey}
          />
        </div>
      </header>

      <OnboardingBanner visible={showOnboarding} />

      <section
        data-testid="dashboard-kpi-band"
        aria-label="Key performance indicators"
        className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
      >
        <WidgetZoneBoundary name="Arrears">
          <Widget_Arrears scope={scope} />
        </WidgetZoneBoundary>
        <WidgetZoneBoundary name="Collection Rate">
          <Widget_CollectionRate scope={scope} />
        </WidgetZoneBoundary>
        <WidgetZoneBoundary name="Reserve Fund">
          <Widget_ReserveFundPosition scope={scope} />
        </WidgetZoneBoundary>
        <WidgetZoneBoundary name="Cash at Bank">
          <Widget_CashAtBank scope={scope} />
        </WidgetZoneBoundary>
      </section>

      <section
        data-testid="dashboard-middle-band"
        aria-label="Detailed operational widgets"
        className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
      >
        <WidgetZoneBoundary name="Arrears Aging">
          <Widget_ArrearsAging scope={scope} />
        </WidgetZoneBoundary>
        <WidgetZoneBoundary name="Reserve Fund Movement">
          <Widget_ReserveFundMovement scope={scope} />
        </WidgetZoneBoundary>
        <WidgetZoneBoundary name="Upcoming Compliance">
          <Widget_UpcomingCompliance scope={scope} />
        </WidgetZoneBoundary>
        <WidgetZoneBoundary name="Upcoming Meetings">
          <Widget_UpcomingMeetings scope={scope} />
        </WidgetZoneBoundary>
      </section>

      <section
        data-testid="dashboard-activity-band"
        aria-label="Recent activity"
        className="w-full"
      >
        <WidgetZoneBoundary name="Recent Activity">
          <Widget_RecentActivity scope={scope} />
        </WidgetZoneBoundary>
      </section>

      <ChatbotLauncherSlot />
    </div>
  );
}

export default DashboardShell;
